import type { NotificationListItem } from './apiContracts';
import { createNotificationIcon } from './notificationVisual';

export type FilterKey = 'all' | 'unread' | 'read';

function timeGroupLabel(date: Date): string {
	const now = new Date();
	const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
	const yesterday = new Date(today.getTime() - 86_400_000);
	const weekStart = new Date(today.getTime() - 7 * 86_400_000);
	const itemDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());
	if (itemDay >= today) return 'Today';
	if (itemDay >= yesterday) return 'Yesterday';
	if (itemDay >= weekStart) return 'This week';
	return 'Earlier';
}

export function formatLocalTimestamp(iso: string): string {
	const date = new Date(iso);
	if (!Number.isFinite(date.getTime())) return '—';
	const pad = (value: number) => String(value).padStart(2, '0');
	return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

function localTimezoneName(date: Date): string {
	try {
		return new Intl.DateTimeFormat(undefined, { timeZoneName: 'long' }).formatToParts(date).find((part) => part.type === 'timeZoneName')?.value ?? '';
	} catch {
		return '';
	}
}

export function renderSkeleton(root: Document | HTMLElement): void {
	const skeleton = root.querySelector<HTMLElement>('#msgSkeleton');
	const list = root.querySelector<HTMLElement>('#msgList');
	const empty = root.querySelector<HTMLElement>('#msgEmpty');
	if (!skeleton || !list || !empty) return;
	list.replaceChildren();
	empty.hidden = true;
	skeleton.hidden = false;
	skeleton.replaceChildren();
	for (let index = 0; index < 5; index += 1) {
		const row = document.createElement('div');
		row.className = 'msg-skeleton-row';
		row.innerHTML = '<div class="msg-skeleton-icon"></div><div class="msg-skeleton-lines"><div class="msg-skeleton-bar msg-skeleton-bar--w70"></div><div class="msg-skeleton-bar msg-skeleton-bar--w40"></div></div>';
		skeleton.appendChild(row);
	}
}

export function hideSkeleton(root: Document | HTMLElement): void {
	const skeleton = root.querySelector<HTMLElement>('#msgSkeleton');
	if (skeleton) {
		skeleton.hidden = true;
		skeleton.replaceChildren();
	}
}

function bodyId(id: string): string {
	return `msg-notification-body-${[...id].map((character) => character.codePointAt(0)?.toString(16)).join('-')}`;
}

export function renderList(root: Document | HTMLElement, items: NotificationListItem[], expandedId: string | null): void {
	const list = root.querySelector<HTMLElement>('#msgList');
	const empty = root.querySelector<HTMLElement>('#msgEmpty');
	if (!list || !empty) return;
	list.replaceChildren();
	empty.hidden = items.length > 0;
	let currentGroup = '';
	for (const item of items) {
		const date = new Date(item.createdAt);
		const group = Number.isFinite(date.getTime()) ? timeGroupLabel(date) : 'Earlier';
		if (group !== currentGroup) {
			currentGroup = group;
			const header = list.ownerDocument.createElement('h2');
			header.className = 'msg-time-group';
			header.textContent = group;
			list.appendChild(header);
		}
		const row = list.ownerDocument.createElement('article');
		row.className = `msg-row${item.readAt ? '' : ' unread'}`;
		row.dataset.notificationId = item.id;
		const toggle = list.ownerDocument.createElement('button');
		toggle.type = 'button';
		toggle.className = 'msg-row-toggle';
		toggle.dataset.notificationId = item.id;
		toggle.setAttribute('aria-expanded', String(expandedId === item.id));
		toggle.setAttribute('aria-controls', bodyId(item.id));

		const icon = list.ownerDocument.createElement('span');
		icon.className = 'msg-notification-icon';
		icon.appendChild(createNotificationIcon(list.ownerDocument, item.category, 'ntf-category-svg'));
		const content = list.ownerDocument.createElement('div');
		content.className = 'msg-row-content';
		const title = list.ownerDocument.createElement('div');
		title.className = 'msg-row-title';
		title.textContent = item.title;
		const preview = list.ownerDocument.createElement('div');
		preview.className = `msg-row-preview${expandedId === item.id ? ' is-expanded' : ''}`;
		preview.id = bodyId(item.id);
		preview.textContent = item.body;
		content.appendChild(title);
		content.appendChild(preview);
		const meta = list.ownerDocument.createElement('time');
		meta.className = 'msg-row-time';
		meta.dateTime = item.createdAt;
		meta.textContent = formatLocalTimestamp(item.createdAt);
		meta.title = Number.isFinite(date.getTime()) ? localTimezoneName(date) : '';
		toggle.appendChild(icon);
		toggle.appendChild(content);
		toggle.appendChild(meta);
		row.appendChild(toggle);
		list.appendChild(row);
	}
}
